import { useState } from "react";
import "@/App.css";
import { BrowserRouter, useLocation, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import AuthCallback from "./pages/AuthCallback";
import LoginGate from "./components/LoginGate";
import Landing from "./pages/Landing";
import Learn from "./pages/Learn";
import PricingView from "./pages/PricingView";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import HistoryView from "./pages/HistoryView";
import ClassroomView from "./pages/ClassroomView";
import LegalPages from "./pages/LegalPages";
import AdminPanel from "./pages/AdminPanel";
import SchoolHub from "./pages/SchoolHub";
import MarketingConsentModal from "./components/MarketingConsentModal";
import { Toaster } from "sonner";

function AppShell() {
  const { user, loading } = useAuth();
  const [session, setSession] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [view, setView] = useState("landing"); // landing | history | classroom | admin
  const [legalTab, setLegalTab] = useState(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex gap-1.5">
          {[0,1,2].map(i => (
            <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#FFD600] ss-dot" style={{animationDelay:`${i*0.18}s`}}/>
          ))}
        </div>
      </div>
    );
  }

  if (legalTab) {
    return <LegalPages initialTab={legalTab} onBack={() => setLegalTab(null)} />;
  }

  if (!user) return <LoginGate onOpenLegal={(tab) => setLegalTab(tab)} />;

  // Post-login views
  const inner = (() => {
    if (pricing) return <PricingView reason={pricing} onClose={() => setPricing(null)} />;
    if (session) {
      return (
        <Learn
          key={session.historySessionId || `${session.topic}-${session.demo}`}
          topic={session.topic}
          demo={session.demo}
          historySessionId={session.historySessionId}
          onExit={() => setSession(null)}
          onQuotaExceeded={() => { setSession(null); setPricing("quota"); }}
        />
      );
    }
    if (view === "history") {
      return (
        <HistoryView
          onBack={() => setView("landing")}
          onNewTopic={() => setView("landing")}
          onOpen={(historySessionId) => setSession({ historySessionId })}
        />
      );
    }
    if (view === "classroom") return <ClassroomView onBack={() => setView("landing")} />;
    if (view === "admin") return <AdminPanel onBack={() => setView("landing")} />;
    if (view === "school") return (
      <SchoolHub
        onBack={() => setView("landing")}
        onOpenPricing={() => setPricing("quota")}
      />
    );
    return (
      <Landing
        onStart={(s) => setSession(s)}
        onOpenPricing={(reason) => setPricing(reason || "default")}
        onOpenHistory={() => setView("history")}
        onOpenClassroom={() => setView("classroom")}
        onOpenAdmin={() => setView("admin")}
        onOpenSchool={() => setView("school")}
        onOpenLegal={(tab) => setLegalTab(tab)}
      />
    );
  })();

  return (
    <>
      {inner}
      <MarketingConsentModal />
    </>
  );
}

function AppRouter() {
  const location = useLocation();

  // Handle OAuth callback (session_id in URL fragment) BEFORE anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <SubscriptionProvider>
      <Routes>
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<AppShell />} />
      </Routes>
    </SubscriptionProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
